import { useState } from 'react';
import { Plus, Search, FolderTree, FileText, Layers, Pencil, Trash2, ArrowRight, Check, X, Upload, CheckCircle, Info } from 'lucide-react';
import PageAiGuide from './PageAiGuide';
import FieldGuide from './FieldGuide';

const KB_HINTS = [
  { kind: 'explain', field: '向量数据库', text: '于部署时已配置好、开箱即用，平台无需单独配 RAG 引擎。' },
  { kind: 'explain', field: 'Embedding 模型', text: '从【模型管理】已启用的向量模型中选；为知识库级配置，强制统一（所有文档共享同一向量空间以保证检索一致）。' },
  { kind: 'explain', field: '解析器', text: '通用（纯文字快）/ 增强（OCR，扫描件用）/ Layout（复杂表格、多栏论文最准但慢）。' },
  { kind: 'recommend', field: '切片策略 / Chunk Size', text: '通用场景用语义分割，块大小适中。', value: '语义分割 · Chunk 1024 · Overlap 100' },
  { kind: 'recommend', field: '重排 TopK / 相似度阈值', text: '检索召回的返回数与过滤阈值。', value: 'TopK 5 / 阈值 0.6' },
];

const CATS = [{ name: '全部分类', n: 36 }, { name: '一级分类', n: 3 }, { name: '一级分类', n: 3 }, { name: '一级分类', n: 3 }, { name: '一级分类', n: 3 }];
const KBS = [
  { id: 1, name: '智能运维指标体系', domain: '科技领域', parser: '增强解析', split: '白动分段（按语义）', emb: 'BGE-Large-ZH', docs: 12, slices: 156, time: '2025-03-11 10:30' },
  { id: 2, name: '金融产品手册', domain: '金融领域', parser: '增强解析', split: '白动分段（按语义）', emb: 'BGE-Large-ZH', docs: 12, slices: 156, time: '2025-03-11 10:30' },
  { id: 3, name: '企业规章制度汇编', domain: '规章制度', parser: '增强解析', split: '白动分段（按语义）', emb: 'BGE-Large-ZH', docs: 12, slices: 156, time: '2025-03-11 10:30' },
  { id: 4, name: '医疗临床指南', domain: '医疗领域', parser: '增强解析', split: '白动分段（按语义）', emb: 'BGE-Large-ZH', docs: 12, slices: 156, time: '2025-03-11 10:30' },
  { id: 5, name: '法律法规知识库', domain: '法律领域', parser: 'Layout解析', split: '基于标识切分', emb: 'penAI Ada-002', docs: 12, slices: 156, time: '2025-03-11 10:30' },
  { id: 6, name: '教育培训资料', domain: '资讯科技', parser: '增强解析', split: '递归字符切分', emb: 'BGE-Base-ZH', docs: 12, slices: 156, time: '2025-03-11 10:30' },
];

const STEPS = ['基础信息', '文档上传', '配置向量空间', '解析与切片', '分段预览'];

// ── 新建知识库（5 步向导）──────────────────────────────────────────────────
function CreateWizard({ open, onClose, onDone }) {
  const [step, setStep] = useState(0);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[760px] max-h-[90vh] bg-white rounded-xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">新建知识库</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-400"><X className="w-5 h-5" /></button>
        </div>

        {/* 步骤条 */}
        <div className="flex items-center px-6 py-4 bg-gray-50">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <div className="flex items-center gap-1.5">
                <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center ${i < step ? 'bg-green-500 text-white' : i === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>{i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}</span>
                <span className={`text-xs ${i === step ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-green-400' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: 320 }}>
          {step === 0 && (
            <div className="space-y-4 max-w-lg">
              <div><label className="block text-sm text-gray-600 mb-1"><span className="text-red-500">* </span>知识库名称</label>
                <FieldGuide label="知识库名称" meta={{ nature: 'info', explain: '该知识库的业务名称，建议体现领域与用途（如「智能运维指标体系」），便于在智能体绑定知识库时辨识。' }}>
                  <input placeholder="请输入" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </FieldGuide>
              </div>
              <div><label className="block text-sm text-gray-600 mb-1"><span className="text-red-500">* </span>领域分类</label>
                <FieldGuide label="领域分类" meta={{ nature: 'info', explain: '用于知识库归类与检索范围圈定；同领域知识库可在智能体侧批量挂载。仅作组织管理，不影响向量化效果。' }}>
                  <select className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"><option>请选择领域</option><option>科技领域</option><option>金融领域</option></select>
                </FieldGuide>
              </div>
              <div><label className="block text-sm text-gray-600 mb-1">描述</label><textarea rows={2} placeholder="简要描述知识库的用途…" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none" /></div>
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-gray-600 space-y-1">
                <div className="font-medium text-gray-700">配置层级说明</div>
                <div><span className="text-amber-600 font-medium">知识库级（全局）</span>：Embedding 模型 + 向量数据库——强制统一，单次批量上传文档沿用同一配置</div>
                <div><span className="text-blue-600 font-medium">文档级（可覆盖）</span>：解析器 + 切片策略——默认继承库配置，上传时可自定义</div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center text-gray-400"><Upload className="w-8 h-8 mx-auto mb-2" /><p className="text-sm">拖拽或点击上传文档</p><p className="text-xs mt-1">支持 .doc/.docx/.pdf/.md/.txt/.ppt/.xls/.html 等，单文件≤20MB，最多5个</p></div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Embedding 模型与向量数据库为知识库级配置，所有文档使用相同向量化方案以确保检索一致性。</p>
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">向量数据库（部署时已配置，开箱即用）</div>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2 text-sm text-gray-600"><Layers className="w-4 h-4 text-purple-500" /> 内置向量数据库 · 已连接</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Embedding 模型（来自模型管理已启用的向量模型，单选）</div>
                <div className="grid grid-cols-2 gap-2">
                  {['BGE-Large-ZH（1536维）', 'OpenAI Ada-002（1536维）'].map((m, i) => (
                    <label key={m} className={`flex items-center gap-2 p-3 border rounded-lg text-sm cursor-pointer ${i === 0 ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`}><input type="radio" name="emb" defaultChecked={i === 0} className="accent-blue-600" />{m}</label>
                  ))}
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">全局默认值：以下配置作为所有新上传文档的默认解析方案，单个文档可在上传时覆盖。</p>
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">文档解析器</div>
                <div className="grid grid-cols-3 gap-2">{['通用解析', '增强解析', 'LayOut解析'].map((p, i) => <label key={p} className={`p-3 border rounded-lg text-sm text-center cursor-pointer ${i === 1 ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`}><input type="radio" name="parser" defaultChecked={i === 1} className="hidden" />{p}</label>)}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">切片策略</div>
                <div className="grid grid-cols-2 gap-2">{['语义分割', '基于标识符切分', '递归字符切分', '文档结构化切分'].map((p, i) => <label key={p} className={`p-3 border rounded-lg text-sm text-center cursor-pointer ${i === 0 ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`}><input type="radio" name="split" defaultChecked={i === 0} className="hidden" />{p}</label>)}</div>
                <div className="mt-3"><label className="text-xs text-gray-500">最大分块大小：1024</label><input type="range" min="256" max="8192" defaultValue="1024" className="w-full accent-blue-600" /></div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div>
              <div className="flex items-center gap-4 mb-3 text-xs text-gray-500"><span>上传文件 3</span><span>预计切片 156</span><span>总字符 48,200</span><span>Chunk 1024</span><span>Overlap 100</span></div>
              <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="p-3 bg-gray-50 border border-gray-200 rounded-lg"><div className="flex items-center justify-between text-xs text-gray-400 mb-1"><span>#切片 {i} · ID kb-001-{i}</span><span>字符 512 · Token 380</span></div><p className="text-xs text-gray-600 line-clamp-2">智能运维指标体系包含告警密度、KPI 偏离度、变更成功率等核心指标，用于衡量网络健康度与运维质量…</p></div>)}</div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button onClick={step === 0 ? onClose : () => setStep(step - 1)} className="px-5 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">{step === 0 ? '取消' : '上一步'}</button>
          <span className="text-xs text-gray-400">步骤 {step + 1} / {STEPS.length}</span>
          {step < STEPS.length - 1
            ? <button onClick={() => setStep(step + 1)} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1">下一步 <ArrowRight className="w-4 h-4" /></button>
            : <button onClick={() => { onDone && onDone(); onClose(); }} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">确认创建</button>}
        </div>
      </div>
    </div>
  );
}

export default function KnowledgeBase() {
  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState('');
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2400); };

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <CreateWizard open={createOpen} onClose={() => setCreateOpen(false)} onDone={() => showToast('知识库创建成功！正在处理文档解析和向量化任务…')} />
      {toast && <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />{toast}</div>}

      <h2 className="text-xl font-bold text-gray-900 mb-1">知识库管理</h2>
      <p className="text-sm text-gray-500 mb-5 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> 向量数据库于部署时已配置好，无需在平台单独配 RAG 引擎；Embedding/重排模型在【模型管理】启用。</p>

      <div className="flex gap-5">
        {/* 左侧分类树 */}
        <aside className="w-56 flex-shrink-0">
          <div className="relative mb-3"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" /><input placeholder="请输入关键字搜索分类" className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg" /></div>
          <div className="space-y-0.5">
            {CATS.map((c, i) => (
              <div key={i} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm cursor-pointer ${i === 0 ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                <FolderTree className="w-3.5 h-3.5" /><span className="flex-1">{c.name}</span><span className="text-xs text-gray-400">({c.n})</span>
              </div>
            ))}
          </div>
        </aside>

        {/* 右侧知识库卡片 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <div className="relative w-72"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><input placeholder="请输入关键字搜索" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
            <button onClick={() => setCreateOpen(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"><Plus className="w-4 h-4" /> 添加知识库</button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {KBS.map(kb => (
              <div key={kb.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-blue-600" /></div>
                  <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-gray-800 truncate">{kb.name}</div><div className="text-xs text-gray-400">{kb.domain}</div></div>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{kb.parser}</span>
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{kb.split}</span>
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{kb.emb}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-50 pt-2">
                  <span>📄 {kb.docs} 文档 · {kb.slices} 切片</span>
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => showToast('打开编辑')} className="hover:text-blue-500"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => showToast('已删除')} className="hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => showToast('进入详情')} className="hover:text-blue-500"><ArrowRight className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="text-xs text-gray-300 mt-1">{kb.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PageAiGuide pageName="知识库管理" hints={KB_HINTS} />
    </div>
  );
}
